const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

export const JTL_CANCELLED_ORDER_STATUSES = [4, 8, 9];

export const isCancelledDashboardStatus = (status) => toNumber(status) === 1;

export const isCancelledJtlOrderStatus = (status) => JTL_CANCELLED_ORDER_STATUSES.includes(toNumber(status));

export const getDashboardOrderStatus = (rowOrStatus) => {
    const status = typeof rowOrStatus === 'object' && rowOrStatus !== null
        ? rowOrStatus.nAuftragStatus ?? rowOrStatus.order_status
        : rowOrStatus;
    return isCancelledJtlOrderStatus(status) ? 1 : 0;
};

export const isCancelledOrder = (rowOrStatus) => {
    if (typeof rowOrStatus === 'object' && rowOrStatus !== null) {
        if (hasOwn(rowOrStatus, 'status')) return isCancelledDashboardStatus(rowOrStatus.status);
        if (hasOwn(rowOrStatus, 'nAuftragStatus')) return isCancelledJtlOrderStatus(rowOrStatus.nAuftragStatus);
        if (hasOwn(rowOrStatus, 'order_status')) return isCancelledJtlOrderStatus(rowOrStatus.order_status);

        const label = String(rowOrStatus.status_label || '').toLowerCase();
        return label.includes('storniert') || label.includes('cancel');
    }

    return isCancelledDashboardStatus(rowOrStatus);
};

export const getOrderStatusLabel = (rowOrStatus) => (isCancelledOrder(rowOrStatus) ? 'Storniert' : 'Aktiv');

export const getPaymentStatusMeta = (value) => {
    const text = String(value || '').trim().toLowerCase();
    const paid = value === true
        || toNumber(value) > 0
        || text === 'ja'
        || (text.includes('bezahlt') && !text.includes('offen'));

    return paid
        ? { key: 'paid', label: 'Bezahlt', tone: 'green' }
        : { key: 'open', label: 'Zahlung offen', tone: 'amber' };
};

export const getPickStatusMeta = (value) => (
    toNumber(value) > 0
        ? { key: 'in_pick', label: 'In Pick', tone: 'blue' }
        : { key: 'not_picked', label: 'Nicht gepickt', tone: 'amber' }
);

export const getShippingStatusMeta = (value) => {
    const status = toNumber(value);
    if (status >= 2) return { key: 'ready', label: 'Versandbereit', tone: 'green' };
    if (status === 1) return { key: 'partial', label: 'Teillieferbar', tone: 'amber' };
    return { key: 'open', label: 'Offen', tone: 'neutral' };
};

export const getDeliveryStatusMeta = (row = {}) => {
    if (toNumber(row.nKomplettAusgeliefert ?? row.completed) === 1) {
        return { key: 'completed', label: 'Ausgeliefert', tone: 'green' };
    }
    if (toNumber(row.nIstVersendet ?? row.shipped) === 1) {
        return { key: 'shipped', label: 'Versendet', tone: 'blue' };
    }
    return { key: 'created', label: 'Erstellt', tone: 'neutral' };
};

export const getShippingBacklogStatusMeta = (row = {}) => {
    const shipping = getShippingStatusMeta(row.nVersandstatusEigen ?? row.shipping_status);
    if (shipping.key !== 'open') return shipping;

    const pick = getPickStatusMeta(row.nPickstatus ?? row.pick_status);
    if (pick.key === 'in_pick') return pick;

    const payment = getPaymentStatusMeta(row.nIstBezahlt ?? row.is_paid);
    if (payment.key === 'open') return payment;

    return shipping;
};

export const JTL_STATUS_MATRIX_SECTIONS = [
    {
        id: 'order',
        title: 'Auftrag',
        fields: ['nAuftragStatus', 'status', 'status_label'],
        summary: 'Beschreibt Auftrag/Storno-Labels für Reports und Exportdaten.',
        rules: [
            { when: 'nAuftragStatus 4, 8 oder 9', result: 'Storniert' },
            { when: 'alle anderen nAuftragStatus-Werte', result: 'Aktiv' },
            { when: 'dashboard status = 1', result: 'Storniert' },
            { when: 'dashboard status = 0', result: 'Aktiv' }
        ]
    },
    {
        id: 'payment',
        title: 'Zahlung',
        fields: ['nIstBezahlt'],
        summary: 'Wird in Versand-Backlog, Reports und operativen Listen als Zahlungszustand genutzt.',
        rules: [
            { when: 'nIstBezahlt > 0', result: 'Bezahlt' },
            { when: 'nIstBezahlt = 0 oder leer', result: 'Zahlung offen' }
        ]
    },
    {
        id: 'pick',
        title: 'Pickstatus',
        fields: ['nPickstatus'],
        summary: 'Zeigt, ob ein Auftrag bereits in der Kommissionierung ist.',
        rules: [
            { when: 'nPickstatus > 0', result: 'In Pick' },
            { when: 'nPickstatus = 0 oder leer', result: 'Nicht gepickt' }
        ]
    },
    {
        id: 'shipping',
        title: 'Versandstatus',
        fields: ['nVersandstatusEigen'],
        summary: 'Beschreibt Lieferbarkeit im Versand-Backlog und in Versand Performance.',
        rules: [
            { when: 'nVersandstatusEigen >= 2', result: 'Versandbereit' },
            { when: 'nVersandstatusEigen = 1', result: 'Teillieferbar' },
            { when: 'nVersandstatusEigen = 0 oder leer', result: 'Offen' }
        ]
    },
    {
        id: 'delivery',
        title: 'Lieferung',
        fields: ['nIstVersendet', 'nKomplettAusgeliefert'],
        summary: 'Gilt für Lieferschein- und Versandabschluss-Ansichten.',
        rules: [
            { when: 'nKomplettAusgeliefert = 1', result: 'Ausgeliefert' },
            { when: 'nIstVersendet = 1', result: 'Versendet' },
            { when: 'sonst', result: 'Erstellt' }
        ]
    },
    {
        id: 'backlog',
        title: 'Operative Versandampel',
        fields: ['nVersandstatusEigen', 'nPickstatus', 'nIstBezahlt'],
        summary: 'Kombinierter Status für offene Versandaufträge und Exportspalten.',
        rules: [
            { when: 'Versandstatus ist bereit oder teillieferbar', result: 'Versandstatus gewinnt' },
            { when: 'sonst nPickstatus > 0', result: 'In Pick' },
            { when: 'sonst Zahlung offen', result: 'Zahlung offen' },
            { when: 'sonst', result: 'Offen' }
        ]
    }
];
